import type { ChainId } from '@entities/network';

export const OFF_RAMP_RAILS = ['SEPA', 'ACH'] as const;

export type OffRampRailCode = (typeof OFF_RAMP_RAILS)[number];

export const OFF_RAMP_TRANSFER_STATUSES = [
  'INITIATED',
  'QUOTED',
  'CONFIRMING',
  'SENT',
  'PROCESSING',
  'DONE',
  'SEND_FAILED',
  'FAILED',
  'EXPIRED',
  'REFUND_PENDING',
  'REFUNDED',
  'REFUND_DONE',
] as const;

export type OffRampTransferStatus = (typeof OFF_RAMP_TRANSFER_STATUSES)[number] | 'UNKNOWN';

export interface OffRampRail {
  rail: OffRampRailCode;
  /** Payout currency, e.g. `EUR`. */
  currency: string;
  /** Minor-unit exponent of `currency` (2 for EUR/USD). */
  precision: number;
  minAmountMinor: bigint;
  maxAmountMinor: bigint;
  providerFeeStatic: number;
  providerFeePercent: number;
  providerFeeCurrency: string;
  platformFeeBps: number;
}

export interface OffRampEstimate {
  rail: OffRampRailCode;
  targetCurrency: string;
  destAmountMinor: bigint;
  /**
   * Payout currency per one unit of crypto, as quoted by the provider — e.g.
   * `0.844272` EUR per USDT, so the USDT cost is `amount / rate` + fees. Not the
   * other way round: `rateDisplay` inverts it for "1 EUR ≈ … USDT".
   */
  providerRate: string;
  providerFeeUsdtRaw: bigint;
  providerSourceUsdtRaw: bigint;
  ownFeeUsdcRaw: bigint;
  /**
   * Total USDT debited from the wallet. Already includes the merchant deposit
   * fee (~2.5%), which arrives in no field of its own — never recompute it as
   * `providerSourceUsdtRaw + ownFeeUsdcRaw` (TRON-002 §3). The `Usdc` in the
   * name is historical: it is the wire name, the asset is USDT on TRON.
   */
  sourceUsdcRaw: bigint;
}

export interface OffRampBeneficiary {
  beneficiaryName: string;
  bankName: string;
  /** First 4 + last 4, e.g. `DE89••••3000`. */
  ibanMasked: string;
  bicSwift: string;
  destinationCountry: string;
}

export interface OffRampTransfer {
  id: string;
  rail: OffRampRailCode;
  status: OffRampTransferStatus;
  targetCurrency: string;
  destAmountMinor: bigint;
  sourceUsdcRaw: bigint | null;
  ownFeeUsdcRaw: bigint | null;
  providerFeeUsdtRaw: bigint | null;
  /** Same direction as `OffRampEstimate.providerRate`: payout currency per unit of crypto. */
  providerRate: string | null;
  beneficiary: OffRampBeneficiary;
  /** Funding intent to sign via `/intents/:id/sign`; null until `prepare-funding` ran. */
  fundingIntentId: string | null;
  quoteExpiresAt: string | null;
  confirmedAt: string | null;
  /** Stable code the client may branch on. */
  failureReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OffRampRecipient {
  id: string;
  rail: OffRampRailCode;
  label: string | null;
  beneficiaryName: string;
  bankName: string;
  ibanMasked: string;
  bicSwift: string;
  destinationCountry: string;
  street: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  isDefault: boolean;
  createdAt: string;
}

/** Body of `POST /off-ramp/recipients` — the saved-recipient table needs the full block. */
export interface SepaRecipientInput {
  beneficiaryName: string;
  bankName: string;
  iban: string;
  bicSwift: string;
  destinationCountry: string;
  street: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  label?: string;
  isDefault?: boolean;
}

export type SepaRecipientPatch = Partial<SepaRecipientInput>;

/** The bank-side fields — the provider can look these up from the BIC. */
export const SEPA_BANK_FIELDS = [
  'bankName',
  'street',
  'city',
  'stateProvince',
  'postalCode',
  'destinationCountry',
] as const;

export type SepaBankFieldName = (typeof SEPA_BANK_FIELDS)[number];

export type SepaBankFields = Pick<SepaRecipientInput, SepaBankFieldName>;

/**
 * `recipientInline` on `POST /off-ramp`. Only the fields that identify the
 * account are required: the provider fills the bank block from its directory,
 * or answers 400 `OFF_RAMP_INCOMPLETE_BANK_DATA` with what it found and what is
 * missing. A bank block that is sent goes to the provider verbatim.
 */
export type InlineSepaRecipientInput = Pick<
  SepaRecipientInput,
  'beneficiaryName' | 'iban' | 'bicSwift'
> &
  Partial<SepaBankFields>;

export interface FundingFeeQuote {
  feeMode: 'GASLESS_TOKEN' | 'NETWORK_NATIVE';
  feeTokenAddress: string;
  feeTokenDecimals: number;
  networkFeeRaw: bigint;
  feeUsd: string | null;
  sendAmountRaw: bigint | null;
  debitAmountRaw: bigint | null;
  maxSendableRaw: bigint | null;
  validUntil: string;
}

/** The `data` of that 400: what the provider found, and what it still needs. */
export interface IncompleteBankData {
  readonly bankData: Partial<SepaBankFields>;
  readonly missingFields: readonly SepaBankFieldName[];
}

/** Same envelope as the card lists; kept local so the slice does not import another entity. */
export interface Paginated<T> {
  data: T[];
  count: number;
}

/** USDC / USDT raw exponent. */
export const SOURCE_TOKEN_DECIMALS = 6;

/**
 * The funding intent is a TRC-20 USDT transfer on TRON, and the user pays its
 * gas in TRX — there is no sponsorship (TRON-002 §3, §2.4).
 */
export const OFF_RAMP_FUNDING_CHAIN: ChainId = 'tron';
export const OFF_RAMP_SOURCE_SYMBOL = 'USDT';
export const OFF_RAMP_NATIVE_SYMBOL = 'TRX';

/** What the wallet must hold in gas before the transfer can be signed. */
export interface NetworkFeeRequirement {
  /** Upper bound of the network fee, `maxFeeRaw` of `estimate-fee`. */
  readonly requiredRaw: bigint;
  readonly availableRaw: bigint;
  readonly symbol: string;
  readonly decimals: number;
}

const CURRENCY_PRECISION: Record<string, number> = { EUR: 2, USD: 2 };

export function currencyPrecision(code: string): number {
  return CURRENCY_PRECISION[code] ?? 2;
}

/**
 * Whole units → minor units for values the API reports as JSON numbers (rail
 * limits). Balances and amounts never go through here — they arrive as raw
 * strings and are parsed by `RawBigIntSchema`.
 */
export function unitsToMinor(value: number, precision: number): bigint {
  return BigInt(Math.round(value * 10 ** precision));
}

/** Visible steps of the progress timeline. */
export const TRANSFER_STEPS: readonly OffRampTransferStatus[] = [
  'CONFIRMING',
  'SENT',
  'PROCESSING',
  'DONE',
];

export function transferStepIndex(status: OffRampTransferStatus): number {
  switch (status) {
    case 'INITIATED':
    case 'QUOTED':
    case 'CONFIRMING':
      return 0;
    case 'SENT':
      return 1;
    case 'PROCESSING':
      return 2;
    default:
      return 3;
  }
}

/**
 * Monotonic order: a late poll must never rewind a status a WS event advanced.
 *
 * Every real transition is strictly increasing, including the whole refund
 * chain (`SEND_FAILED` → `REFUND_PENDING` → `REFUNDED` → `REFUND_DONE`), so
 * ranks are never shared by two states that follow one another. The three
 * absolute outcomes rank above everything: nothing follows them, and a stale
 * response must not resurrect a finished transfer.
 */
export const TRANSFER_ORDER: Record<OffRampTransferStatus, number> = {
  UNKNOWN: 0,
  INITIATED: 1,
  QUOTED: 2,
  CONFIRMING: 3,
  SENT: 4,
  PROCESSING: 5,
  SEND_FAILED: 6,
  REFUND_PENDING: 7,
  REFUNDED: 8,
  REFUND_DONE: 9,
  DONE: 10,
  FAILED: 10,
  EXPIRED: 10,
};

/**
 * Strictly greater, not `>=`: equal ranks mean "two outcomes of the same
 * stage", and swapping one for the other is exactly the rewind this guards
 * against.
 */
export function advanceTransferStatus(
  current: OffRampTransferStatus,
  next: OffRampTransferStatus,
): OffRampTransferStatus {
  return TRANSFER_ORDER[next] > TRANSFER_ORDER[current] ? next : current;
}

export const IN_FLIGHT_TRANSFER_STATUSES: readonly OffRampTransferStatus[] = [
  'INITIATED',
  'QUOTED',
  'CONFIRMING',
  'SENT',
  'PROCESSING',
  'SEND_FAILED',
  'REFUND_PENDING',
];

export const TERMINAL_TRANSFER_STATUSES: readonly OffRampTransferStatus[] = [
  'DONE',
  'FAILED',
  'EXPIRED',
  'REFUNDED',
  'REFUND_DONE',
];

export function isTransferInFlight(status: OffRampTransferStatus): boolean {
  return IN_FLIGHT_TRANSFER_STATUSES.includes(status);
}

/** Quote expiry as epoch ms; 0 when there is none or it is unparsable. */
export function quoteExpiresAtMs(transfer: OffRampTransfer | null): number {
  if (!transfer?.quoteExpiresAt) return 0;
  const expiresAt = new Date(transfer.quoteExpiresAt).getTime();
  return Number.isFinite(expiresAt) ? expiresAt : 0;
}

/** Seconds until the authoritative quote expires; 0 when unknown or already past. */
export function quoteSecondsLeft(transfer: OffRampTransfer, now = Date.now()): number {
  const expiresAt = quoteExpiresAtMs(transfer);
  if (expiresAt === 0) return 0;
  return Math.max(0, Math.ceil((expiresAt - now) / 1000));
}

/** A `QUOTED` transfer whose quote is still alive can be funded (signed) right now. */
export function awaitsSignature(transfer: OffRampTransfer, now = Date.now()): boolean {
  return transfer.status === 'QUOTED' && quoteSecondsLeft(transfer, now) > 0;
}
